import {Helmet} from 'react-helmet-async'
import {useTranslation} from 'react-i18next'
import {ArrowLeft} from 'lucide-react'
import {Link} from 'react-router-dom'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import {Button} from '@/components/ui/button'
import {Card, CardContent} from '@/components/ui/card'
import {useAuthors} from '@/hooks/useAuthors'
import {urlForImage} from '@/lib/sanity'
import type {AuthorSummary} from '@/types/author'

// A link, not a modal button: the author page's crawlable inbound link (#1014).
const AuthorCard = ({author}: {author: AuthorSummary}) => {
  const src = author.picture?.asset
    ? urlForImage(author.picture).width(400).height(400).fit('crop').auto('format').url()
    : null
  return (
    <Link to={`/authors/${author.slug}`} className="group block h-full text-left w-full focus-ring rounded-lg">
      <Card className="h-full overflow-hidden hover:border-foreground/20 transition-colors text-center">
        <CardContent className="p-6 flex flex-col items-center gap-3">
          <div className="w-28 h-28 rounded-full overflow-hidden bg-secondary border-2 border-primary/15">
            {src ? (
              <img
                src={src}
                alt={author.picture.alt ?? author.name}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : null}
          </div>
          <div>
            <h3 className="font-display font-semibold text-foreground text-lg leading-snug group-hover:text-foreground/80 transition-colors">
              {author.name}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">{author.role}</p>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-3">{author.shortBio}</p>
        </CardContent>
      </Card>
    </Link>
  )
}

const Authors = () => {
  const {t} = useTranslation()
  const {data: authors, isLoading, isError} = useAuthors()

  return (
    <div className="min-h-screen bg-background">
      {/* Title only: the Rails layout owns description/og/twitter (#674). */}
      <Helmet>
        <title>{t('authors.metaTitle')}</title>
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-24 max-w-6xl">
        <Button variant="ghost" size="sm" className="mb-6 -ml-2" asChild>
          <Link to="/">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t('nav.back')}
          </Link>
        </Button>

        <div className="max-w-2xl mb-12">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-3">
            {t('authors.title')}
          </h1>
          <p className="text-lg text-muted-foreground">{t('authors.subtitle')}</p>
        </div>

        {isLoading ? (
          <div className="text-center py-16 text-muted-foreground">{t('authors.loading')}</div>
        ) : isError ? (
          <div className="text-center py-16 text-muted-foreground">
            {t('authors.loadError')}
          </div>
        ) : authors && authors.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {authors.map((author) => (
              <AuthorCard key={author._id} author={author} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-muted-foreground">{t('authors.empty')}</div>
        )}
      </main>
      <Footer />
    </div>
  )
}

export default Authors
